import {
  CaretDownIcon,
  CheckCircleIcon,
  CheckIcon,
  PlusIcon,
  QuestionIcon,
  RocketLaunchIcon,
  TrashIcon,
  WarningOctagonIcon,
  XIcon,
  type Icon,
} from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import classNames from 'classnames'
import { Fragment, useCallback, useState } from 'react'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import * as styles from './resolution-categorization-drawer.css.ts'
import { bucketPaletteScope } from './resolution-categorization-palette.css.ts'
import {
  BUCKET_COLOR_SWATCH_KEYS,
  BUCKET_COLOR_SWATCHES,
  type BucketSwatchKey,
} from './resolution-categorization-palette.ts'
import {
  addBucket,
  aggregateBuckets,
  CATEGORIZATION_PRESET_META,
  getPresetCategorization,
  moveStateToBucket,
  recolorBucket,
  removeBucket,
  renameBucket,
  type Categorization,
  type ResolutionBucket,
} from './resolution-categorization.ts'
import { RESOLUTION_META, type ResolutionCounts, type ResolutionState } from './resolution-taxonomy.ts'

export type ResolutionCategorizationDrawerProps = {
  categorization: Categorization
  onCategorizationChange: (categorization: Categorization) => unknown
  /** Counts shown on each bucket, so the effect of a regrouping is visible while editing. */
  resolution: ResolutionCounts
  /** Called after Done, and after Cancel has restored the categorization the drawer opened with. */
  onClose: () => unknown
}

/**
 * The categorization editor: how the five resolution states group into the named, coloured buckets
 * the panel draws.
 *
 * Every edit is applied immediately rather than staged behind a Save, so the bar and funnel behind
 * the drawer restate themselves as the user works — which is the point of the control, and what the
 * design shows. Done therefore only closes; Cancel writes back the categorization the drawer opened
 * with, then closes. Escape and a click outside the drawer mean Cancel.
 */
export const ResolutionCategorizationDrawer: React.FC<ResolutionCategorizationDrawerProps> = ({
  categorization,
  onCategorizationChange,
  resolution,
  onClose,
}) => {
  // The panel mounts the drawer only while it is open, so the initial state is the categorization
  // as it stood when the drawer opened.
  const [openingCategorization] = useState(categorization)

  const handleCancel = useCallback(() => {
    onCategorizationChange(openingCategorization)
    onClose()
  }, [onCategorizationChange, onClose, openingCategorization])

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        handleCancel()
      }
    },
    [handleCancel]
  )

  const buckets = aggregateBuckets({ categorization, resolution })

  return (
    <Dialog.Root open={true} onOpenChange={handleOpenChange} modal={false}>
      <Dialog.Portal>
        {/* The panel's palette class does not reach a portal, so the drawer carries its own. */}
        <Dialog.Content className={classNames(styles.content, bucketPaletteScope)}>
          <div className={styles.header}>
            <Dialog.Title className={styles.title}>Edit categorization</Dialog.Title>
            <Dialog.Description className={styles.description}>
              Define how the five resolution states group into coloured buckets. Changes apply to the panel and the
              findings table.
            </Dialog.Description>
          </div>

          <section className={styles.section} aria-label="Start from a preset">
            <h3 className={styles.sectionHeading}>Start from a preset</h3>
            <div className={styles.presetGrid}>
              {CATEGORIZATION_PRESET_META.map(preset => (
                <button
                  key={preset.key}
                  type="button"
                  className={classNames(styles.presetCard, {
                    [styles.presetCardSelected]: preset.key === categorization.presetKey,
                  })}
                  aria-pressed={preset.key === categorization.presetKey}
                  // Custom is where edits accumulate rather than a shape to apply, so selecting it
                  // keeps the current buckets instead of resetting them.
                  onClick={() =>
                    preset.key === 'custom'
                      ? onCategorizationChange({ ...categorization, presetKey: 'custom' })
                      : onCategorizationChange(getPresetCategorization(preset.key))
                  }
                >
                  <span className={styles.presetName}>{preset.name}</span>
                  <span className={styles.presetDescription}>{preset.description}</span>
                </button>
              ))}
            </div>
          </section>

          <section className={styles.section} aria-label="Buckets">
            <div className={styles.sectionHeadingRow}>
              <h3 className={styles.sectionHeading}>Buckets</h3>
              <span className={styles.sectionHint}>Click a state to move it · click the dot to recolour</span>
            </div>
            <div className={styles.bucketList}>
              {buckets.map(bucket => (
                <div key={bucket.id} className={styles.bucketRow} role="group" aria-label={`${bucket.name} bucket`}>
                  <ColorPicker
                    swatch={bucket.swatch}
                    bucketName={bucket.name}
                    onSelect={swatch =>
                      onCategorizationChange(recolorBucket({ categorization, bucketId: bucket.id, swatch }))
                    }
                  />
                  <input
                    className={styles.bucketName}
                    value={bucket.name}
                    aria-label={`Name of the ${bucket.name} bucket`}
                    onChange={event =>
                      onCategorizationChange(
                        renameBucket({ categorization, bucketId: bucket.id, name: event.target.value })
                      )
                    }
                  />
                  <span className={styles.bucketCount}>{bucket.count}</span>
                  <div className={styles.stateChips}>
                    {bucket.states.map(state => (
                      <StateChip
                        key={state}
                        state={state}
                        swatch={bucket.swatch}
                        categorization={categorization}
                        currentBucketId={bucket.id}
                        onMove={bucketId =>
                          onCategorizationChange(moveStateToBucket({ categorization, state, bucketId }))
                        }
                      />
                    ))}
                    <AddOutcomeMenu
                      categorization={categorization}
                      bucket={bucket}
                      onAdd={state =>
                        onCategorizationChange(moveStateToBucket({ categorization, state, bucketId: bucket.id }))
                      }
                    />
                  </div>
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={`Remove the ${bucket.name} bucket`}
                    // The last bucket has nowhere to re-home its states, so removal is refused
                    // rather than silently dropping them.
                    disabled={categorization.buckets.length === 1}
                    onClick={() => onCategorizationChange(removeBucket({ categorization, bucketId: bucket.id }))}
                  >
                    <TrashIcon size={16} weight="light" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className={styles.addBucketButton}
              onClick={() => onCategorizationChange(addBucket(categorization))}
            >
              <PlusIcon size={16} weight="bold" />
              Add a bucket
            </button>
          </section>

          <div className={styles.footer}>
            <GhostButton type="secondary" size="small" onClick={handleCancel}>
              Cancel
            </GhostButton>
            <DefaultButton buttonType="primary" size="small" onClick={onClose}>
              Done
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

type ColorPickerProps = {
  swatch: BucketSwatchKey
  bucketName: string
  onSelect: (swatch: BucketSwatchKey) => unknown
}

const ColorPicker: React.FC<ColorPickerProps> = ({ swatch, bucketName, onSelect }) => (
  <DropdownMenu.Root modal={false}>
    <DropdownMenu.Trigger asChild>
      <button
        type="button"
        className={styles.colorDot}
        style={{ backgroundColor: BUCKET_COLOR_SWATCHES[swatch].fill }}
        aria-label={`Colour of the ${bucketName} bucket`}
      />
    </DropdownMenu.Trigger>
    <DropdownMenu.Content className={styles.swatchGrid} align="start" sideOffset={4}>
      {BUCKET_COLOR_SWATCH_KEYS.map(key => (
        <DropdownMenu.Item key={key} asChild onSelect={() => onSelect(key)}>
          <button
            type="button"
            className={styles.swatch}
            style={{ backgroundColor: BUCKET_COLOR_SWATCHES[key].fill }}
            aria-label={BUCKET_COLOR_SWATCHES[key].label}
          />
        </DropdownMenu.Item>
      ))}
    </DropdownMenu.Content>
  </DropdownMenu.Root>
)

type StateChipProps = {
  state: ResolutionState
  /** The swatch of the bucket the chip sits in, which sets both its fill and its text colour. */
  swatch: BucketSwatchKey
  categorization: Categorization
  currentBucketId: string
  onMove: (bucketId: string) => unknown
}

const StateChip: React.FC<StateChipProps> = ({ state, swatch, categorization, currentBucketId, onMove }) => {
  const targets = categorization.buckets.filter(bucket => bucket.id !== currentBucketId)

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={styles.stateChip}
          style={{ backgroundColor: BUCKET_COLOR_SWATCHES[swatch].fill, color: BUCKET_COLOR_SWATCHES[swatch].text }}
          // With one bucket there is nowhere to move to, so the chip stops offering it.
          disabled={targets.length === 0}
          title="Click to move to another bucket"
          aria-label={`Move ${RESOLUTION_META[state].label} to another bucket`}
        >
          {RESOLUTION_META[state].label}
          <CaretDownIcon size={12} weight="bold" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className={styles.moveMenu} align="start" sideOffset={4}>
        <DropdownMenu.Label className={styles.menuLabel}>Move to</DropdownMenu.Label>
        {targets.map(target => (
          <DropdownMenu.Item key={target.id} className={styles.moveMenuItem} onSelect={() => onMove(target.id)}>
            <span
              className={styles.moveMenuDot}
              style={{ backgroundColor: BUCKET_COLOR_SWATCHES[target.swatch].fill }}
            />
            {target.name}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

const OUTCOME_ICONS: Record<Exclude<ResolutionState, 'dismissed'>, Icon> = {
  fixed: CheckCircleIcon,
  fixReady: RocketLaunchIcon,
  acknowledged: WarningOctagonIcon,
  untriaged: QuestionIcon,
}

/**
 * Dismissed's X is drawn in the swatch's text colour on a circle in its fill, rather than cut out of
 * a filled `XCircleIcon`: a cut-out shows the menu behind it, and on the Neutral dark swatch that
 * leaves a near-black X on a dark-grey circle. Prohibit is not used because it means blocked elsewhere.
 */
const OutcomeIcon: React.FC<{ state: ResolutionState }> = ({ state }) => {
  const swatch = BUCKET_COLOR_SWATCHES[RESOLUTION_META[state].swatch]

  if (state === 'dismissed') {
    return (
      <span className={styles.outcomeIconBadge} style={{ backgroundColor: swatch.fill }}>
        <XIcon size={9} weight="bold" color={swatch.text} />
      </span>
    )
  }

  const Glyph = OUTCOME_ICONS[state]
  return <Glyph size={14} weight="fill" color={swatch.fill} />
}

type AddOutcomeMenuProps = {
  categorization: Categorization
  bucket: ResolutionBucket
  onAdd: (state: ResolutionState) => unknown
}

/**
 * Pulls an outcome into this bucket from wherever it sits — the inverse of a chip's move menu, and
 * the only way to populate a bucket that has no chips to move.
 */
const AddOutcomeMenu: React.FC<AddOutcomeMenuProps> = ({ categorization, bucket, onAdd }) => {
  const groups = categorization.buckets.filter(group => group.states.length > 0)

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={styles.addOutcomeButton}
          aria-label={`Add an outcome to the ${bucket.name} bucket`}
        >
          <PlusIcon size={12} weight="bold" />
          Add outcome
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className={styles.moveMenu} align="start" sideOffset={4}>
        {groups.map((group, index) => (
          <Fragment key={group.id}>
            {index > 0 && <DropdownMenu.Separator className={styles.menuSeparator} />}
            <DropdownMenu.Group>
              <DropdownMenu.Label className={styles.menuLabel}>{group.name}</DropdownMenu.Label>
              {group.states.map(state => {
                const isInThisBucket = group.id === bucket.id

                return (
                  <DropdownMenu.Item
                    key={state}
                    className={styles.moveMenuItem}
                    disabled={isInThisBucket}
                    onSelect={() => onAdd(state)}
                  >
                    <OutcomeIcon state={state} />
                    {RESOLUTION_META[state].label}
                    {isInThisBucket ? (
                      <CheckIcon className={styles.outcomeCheck} size={14} weight="bold" />
                    ) : (
                      <span className={styles.outcomeLocation}>in {group.name}</span>
                    )}
                  </DropdownMenu.Item>
                )
              })}
            </DropdownMenu.Group>
          </Fragment>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
